import clsx from 'clsx'
import { nothing, unsafeCSS } from 'lit'
import { property } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { html } from 'lit/static-html.js'

import type { AriaCurrent, LinkTarget, SidenavSpacing } from '@/common/types'
import { t } from '@/localization/localization'

import { DdsElement } from '../../internal/dds-hu-element'
import styles from './sidenav-item.scss?inline'

/**
 * `dap-ds-sidenav-item`
 * @summary Side navigation item
 * @element dap-ds-sidenav-item
 * @title - Side navigation item
 * @group side-navigation
 *
 * @slot - The content of the side navigation item.
 * @slot suffix - The suffix of the side navigation item.
 *
 * @csspart base - The main side navigation item container.
 * @csspart link - The link of the side navigation item.
 *
 * @cssproperty --dds-sidenav-item-min-height-xs - Minimum height of the sidenav item (default: var(--dds-spacing-800))
 * @cssproperty --dds-sidenav-item-min-height-sm - Minimum height of the sidenav item (default: var(--dds-spacing-1000))
 * @cssproperty --dds-sidenav-item-min-height-lg - Minimum height of the sidenav item (default: var(--dds-spacing-1200))
 * @cssproperty --dds-sidenav-item-margin-bottom - Default bottom margin of the sidenav item (default: var(--dds-spacing-100))
 * @cssproperty --dds-sidenav-item-padding-xs - Padding of the sidenav item (default: var(--dds-spacing-100) var(--dds-spacing-300))
 * @cssproperty --dds-sidenav-item-padding-sm - Padding of the sidenav item (default: var(--dds-spacing-200) var(--dds-spacing-400))
 * @cssproperty --dds-sidenav-item-padding-lg - Padding of the sidenav item (default: var(--dds-spacing-300) var(--dds-spacing-500))
 * @cssproperty --dds-sidenav-item-radius - Border radius of the sidenav item (default: var(--dds-radius-base))
 * @cssproperty --dds-sidenav-item-color - Text color of the sidenav item (default: var(--dds-text-neutral-base))
 * @cssproperty --dds-sidenav-item-font-size-xs - Font size of the sidenav item (default: var(--dds-font-sm))
 * @cssproperty --dds-sidenav-item-font-size-sm - Font size of the sidenav item (default: var(--dds-font-base))
 * @cssproperty --dds-sidenav-item-font-size-lg - Font size of the sidenav item (default: var(--dds-font-lg))
 * @cssproperty --dds-sidenav-item-font-weight - Font weight of the sidenav item (default: var(--dds-font-weight-medium))
 * @cssproperty --dds-sidenav-item-spacing-margin - Margin used for spacing variants (default: var(--dds-spacing-400))
 * @cssproperty --dds-sidenav-item-bg-active - Background color when the item is active (default: var(--dds-transparent-black-subtle))
 * @cssproperty --dds-sidenav-item-bg-hover - Background color on hover and active states (default: var(--dds-transparent-black-base))
 */
export default class DapDSSideNavItem extends DdsElement {
  static tagName = 'dap-ds-sidenav-item'

  /** The href of the side navigation item */
  @property() href: string

  /** Whether the side navigation item is active */
  @property({ type: Boolean, reflect: true }) active = false

  /**
   * Which `aria-current` token to expose on the link when `active` is set.
   *
   * `aria-current` has to sit on the anchor that assistive technology actually
   * exposes. Setting it on this host does nothing: the host is a role-less custom
   * element, so the attribute is stranded there and screen-reader users cannot tell
   * which item is current. Use `current="step"` inside a stepper.
   *
   * @type {'page' | 'step' | 'location' | 'date' | 'time' | 'true'}
   */
  @property({ reflect: true }) current: AriaCurrent = 'page'

  /** The spacing of the side navigation item
   * @type {'top' | 'bottom' | 'both' | 'none'}
   */
  @property({ reflect: true }) spacing: SidenavSpacing = 'none'

  /** The size of the side navigation item */
  @property({ reflect: true, type: String }) size: 'xs' | 'sm' | 'lg' = 'xs'

  /** The target of the link.
   * @type {'_blank' | '_self' | '_parent' | '_top'}
   */
  @property() target: LinkTarget = '_self'

  /** The rel of the link */
  @property({ type: String }) rel = 'noreferrer noopener'

  /** The text for the target blank link, applicable when target is _blank. PRO TIP: Use parenthesis to wrap the text */
  @property({ type: String }) targetBlankText: string

  static readonly styles = unsafeCSS(styles)

  render() {
    return html`
      <li
        part="base"
        role="listitem"
        class=${clsx(
          'sidenav-item',
          `sidenav-item--spacing-${this.spacing}`,
          `sidenav-item--${this.size}`,
          {
            'sidenav-item--active': this.active,
          },
        )}>
        <a
          part="link"
          href=${this.href}
          class="sidenav-item__link"
          aria-current=${ifDefined(this.active ? this.current : undefined)}
          target=${ifDefined(this.href ? this.target : undefined)}
          rel=${ifDefined(this.href ? this.rel : undefined)}>
          <slot></slot>
          ${this.target === '_blank'
            ? html`<span class="sr-only">
                ${this.targetBlankText ?? t('opensInNewTab')}</span
              >`
            : nothing}
          <slot name="suffix"></slot>
        </a>
      </li>
    `
  }
}
